import { cloneDeep } from "lodash"

/**
 * Caches frequently used API requests. Acts as a wrapper for selected API service methods.
 * Automatically clears expired resources from the cache at a set interval.
 */
export default abstract class ApiCache {
	protected abstract cacheName: string

	private cache: { [key: string]: { methodName: string, data: Promise<unknown>, expiresAt: Date } } = {}
	protected readonly cacheTimeMins: number = 5

	// Do not modify directly, use debugForceSkipCache for debugging purposes.
	private _skipCache = false

	protected readonly enableDebugLogging = false
	protected readonly debugForceSkipCache = false

	protected readonly cleanupIntervalMins = 1
	protected cleanupAdditionalActions: (() => void)[] = []
	private cleanupInterval: NodeJS.Timeout

	protected clearAdditionalActions: (() => void)[] = []

	public constructor () {
		// Clean up resources at a set interval.
		this.cleanupInterval = setInterval(() => {
			this.cleanupResources()
		}, this.cleanupIntervalMs)

		if (this.debugForceSkipCache) {
			this.skipCache = true
			this.logDebugMessage("Force skip cache is enabled")
		}
	}

	protected get cacheTimeMs (): number {
		return this.cacheTimeMins * 60 * 1000
	}

	protected get cleanupIntervalMs (): number {
		return this.cleanupIntervalMins * 60 * 1000
	}

	/**
	 * Clears the cache.
	 */
	public clear () {
		this.cache = {}

		// Run any additional cleanup actions.
		this.clearAdditionalActions.forEach(action => action())
	}

	/**
	 * Gets whether the cache is being skipped.
	 */
	public get skipCache () {
		return this._skipCache || this.debugForceSkipCache
	}

	/**
	 * Sets whether to skip the cache.
	 */
	public set skipCache (value: boolean) {
		if (this.debugForceSkipCache) value = true

		const hasChanged = this._skipCache !== value
		if (!hasChanged) return

		this._skipCache = value
		if (value) this.clear()
		this.logDebugMessage(`Skip cache set to ${value}`)
	}

	/**
	 * Clears the cache for a given list of method names.
	 * @param methodNames The names of the methods to clear the cache for.
	 */
	public clearForMethods (methodNames: string[]) {
		this.logDebugMessage(`Clearing cache for methods ${methodNames}`)
		methodNames.forEach(methodName => {
			Object.keys(this.cache).forEach(key => {
				if (this.cache[key].methodName === methodName) {
					delete this.cache[key]
				}
			})
		})
	}

	public triggerCleanup () {
		this.logDebugMessage("Triggering cleanup")
		this.cleanupResources()
	}

	/**
	 * @summary Gets a resource, either from the cache if it has already been fetched and has not expired,
	 * or otherwise fetches it.
	 * @param methodName The name of the calling method.
	 * @param paramsObject An object containing the parameters for the fetch method.
	 * @param fetchFunction The function to call to fetch the resource if it is not in the cache.
	 * @param cacheOptions Additional options for caching.
	 * @returns A promise that will resolve to the requested resource.
	 */
	protected getOrFetchAndStore (methodName: string, paramsObject: object, fetchFunction: () => Promise<unknown>, cacheOptions?: ApiCacheOptions) {
		if (this.skipCache) return fetchFunction()

		const cacheKey = this.getCacheKey(methodName, paramsObject, false)
		let cachedData: unknown
		if (!cacheOptions?.forceRefetch) cachedData = this.tryRetrieve(cacheKey)
		if (cachedData !== undefined) {
			this.logDebugMessage(`Retrieved cached data for ${methodName} ${cacheKey}`)
			return cachedData as Promise<unknown>
		}

		const freshDataPromise = this.fetchAndStore(methodName, this.getParamsJson(paramsObject), fetchFunction, cacheOptions)
		return freshDataPromise.then(data => cloneDeep(data))
	}

	/**
	 * Fetches a resource and stores it in the cache.
	 * @param methodName The name of the calling method.
	 * @param paramsJson A JSON string representation of the parameters for the fetch method.
	 * @param fetchFunction The function to call to fetch the resource.
	 * @param cacheOptions Additional options for caching.
	 * @returns A promise that will resolve to the requested resource.
	 */
	protected fetchAndStore (methodName: string, paramsJson: string, fetchFunction: () => Promise<unknown>, cacheOptions?: ApiCacheOptions) {
		if (this.skipCache) return fetchFunction()

		const dataPromise = this.fetch(methodName, paramsJson, fetchFunction)
		this.store(methodName, paramsJson, dataPromise, cacheOptions)
		return dataPromise
	}

	/**
	 * Fetches a resource.
	 * @param methodName The name of the calling method.
	 * @param paramsJson A JSON string representation of the parameters for the fetch method.
	 * @param fetchFunction The function to call to fetch the resource.
	 * @returns A promise for the requested resource.
	 */
	protected fetch (methodName: string, paramsJson: string, fetchFunction: () => Promise<unknown>) {
		this.logDebugMessage(`Fetching data for ${methodName} ${paramsJson}`)
		return fetchFunction()
	}

	/**
	 * Stores a resource in the cache.
	 * @param methodName The name of the calling method.
	 * @param paramsJson A JSON string representation of the parameters for the fetch method (used as a key).
	 * @param data The data to store.
	 * @param cacheOptions Additional options for caching.
	 */
	protected store (methodName: string, paramsJson: string, data: Promise<unknown>, cacheOptions?: ApiCacheOptions) {
		const requestCacheTimeMs = cacheOptions?.cacheTimeMins !== undefined
			? cacheOptions.cacheTimeMins * 60 * 1000
			: this.cacheTimeMs

		const cacheKey = this.getCacheKey(methodName, paramsJson, true)
		this.cache[cacheKey] = {
			methodName,
			data,
			expiresAt: new Date(Date.now() + requestCacheTimeMs)
		}
	}

	/**
	 * Attempts to retrieve a resource from the cache.
	 * @param cacheKey A unique key composed of the name and a JSON
	 * string representation of the parameters for the fetch method.
	 * @returns The cached data if it exists and has not expired, otherwise undefined.
	 */
	protected tryRetrieve (cacheKey: string): Promise<unknown> | undefined {
		if (this.cache[cacheKey] && this.cache[cacheKey].expiresAt > new Date(Date.now())) {
			return this.cache[cacheKey].data
		}

		return undefined
	}

	protected logDebugMessage (message: string) {
		if (this.enableDebugLogging) console.log(`${this.cacheName} [DEBUG]: ${message}`)
	}

	protected logError (error: Error) {
		console.error(`${this.cacheName} [ERROR]:`, error)
	}

	/**
	 * Gets a JSON string representation of an object.
	 * @param paramsObject The object to stringify.
	 * @returns A JSON string representation of the object.
	 */
	protected getParamsJson (paramsObject: object) {
		return JSON.stringify(paramsObject, Object.keys(paramsObject).sort())
	}

	/**
	 * Gets a unique cache key for a method and its parameters.
	 * @param methodName The name of the fetch method.
	 * @param params The parameters for the fetch method, either as an object or a JSON string.
	 * @param paramsIsJson Whether the params parameter is already a JSON string.
	 * @returns A unique cache key.
	 */
	protected getCacheKey (methodName: string, params: object | string, paramsIsJson: boolean = false) {
		const paramsJson = paramsIsJson
			? params as string
			: this.getParamsJson(params as object)

		return `${methodName}-${paramsJson}`
	}

	/**
	 * @summary Cleans up expired resources from the cache.
	 */
	protected cleanupResources (): void {
		const now = new Date(Date.now())

		// Remove expired resources.
		Object.keys(this.cache).forEach((key) => {
			if (this.cache[key].expiresAt <= now) {
				const cachedItem = this.cache[key]
				this.logDebugMessage(`Deleted expired resource ${cachedItem.methodName} ${key}`)
				delete this.cache[key]
			}
		})

		// Run any additional cleanup actions.
		this.cleanupAdditionalActions.forEach(action => action())
	}
}

export interface ApiCacheOptions {
	cacheTimeMins?: number
	forceRefetch?: boolean
}

/**
 * A list of all API caches in use.
 */
export const apiCaches: ApiCache[] = []

/**
 * Sets whether to skip the cache for all API caches.
 * @param skip Whether to skip API caching.
 */
export function setSkipAllApiCaches (skip: boolean) {
	apiCaches.forEach(cache => cache.skipCache = skip)
}

/**
 * Clears all API caches.
 */
export function clearAllApiCaches () {
	apiCaches.forEach(cache => cache.clear())
}
